import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { useWorkerNotices } from "@/hooks/useWorkerNotices";
import { parseCurrentWeather, parseHourlyWeather, describeWeather } from "@/services/weather";
import { workerService, type WorkerSiteResponse } from "@/services/worker.service";
import type { ParsedWeather, HourlyWeather } from "@/services/weather.types";

/**
 * 근로자용 현장 페이지 (#102/#103/#112).
 *
 * 현장 UUID(QR)로 진입하는 비인증 경량 페이지.
 * - GET /worker/{uuid}/site · weather 로 현장정보·날씨 조회 (read-only)
 * - "알림 받기" 시 구독을 현장(uuid)에 귀속 (usePushSubscription)
 *
 * PWA: worker 앱은 자체 manifest(start_url=/worker)라 동적 교체 불필요.
 * 현장(uuid)은 localStorage 로 기억 → 설치 앱은 마지막 현장으로 시작.
 * 현장 이동 = 새 QR → uuid 갱신 + 구독 자동 재등록(usePushSubscription 내부).
 */

const STORAGE_KEY = "worker-site-uuid";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidSiteUuid = (v: string | null | undefined): v is string => !!v && UUID_RE.test(v);

export function WorkerPage() {
  const { uuid: uuidParam } = useParams();
  // URL(QR)의 현장이 우선, 없으면 마지막에 저장한 현장(localStorage).
  const storedUuid = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const uuid = uuidParam ?? storedUuid ?? "";
  const validUuid = isValidSiteUuid(uuid);

  const { platform, promptInstall } = usePWAInstall();
  const { subscribed, busy, status, subscribe } = usePushSubscription(validUuid ? uuid : "");
  const { notices, banner, dismissBanner } = useWorkerNotices(validUuid ? uuid : "");

  const [site, setSite] = useState<WorkerSiteResponse | null>(null);
  const [siteError, setSiteError] = useState(false);
  const [currentWeather, setCurrentWeather] = useState<ParsedWeather | null>(null);
  const [hourlyWeather, setHourlyWeather] = useState<HourlyWeather[]>([]);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [installModalDismissed, setInstallModalDismissed] = useState(false);
  const [isStandalone] = useState(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true)
  );

  const justInstalled = platform === "installed" && !isStandalone;
  const notifSupported = typeof window !== "undefined" && "Notification" in window;
  const loading = validUuid && !site && !siteError;
  const showInstallModal = validUuid && !isStandalone && !justInstalled && !installModalDismissed;
  const notifyEligible =
    isStandalone &&
    validUuid &&
    !subscribed &&
    notifSupported &&
    Notification.permission === "default";
  const showNotifyPrompt = notifyEligible && !promptDismissed;

  const weatherDesc = describeWeather(currentWeather?.pty ?? null, currentWeather?.sky ?? null);
  const latestNotice = notices[0] ?? null;

  // QR(URL param)로 진입하면 현장을 기억한다. (현장 이동 = 새 QR → 여기서 갱신)
  useEffect(() => {
    if (isValidSiteUuid(uuidParam)) localStorage.setItem(STORAGE_KEY, uuidParam);
    else if (storedUuid && !isValidSiteUuid(storedUuid)) localStorage.removeItem(STORAGE_KEY);
  }, [uuidParam, storedUuid]);

  // 현장 정보 + 날씨 조회
  useEffect(() => {
    if (!validUuid) return;
    let cancelled = false;
    setSiteError(false);
    workerService
      .getWorkerSite(uuid)
      .then((s) => {
        if (cancelled) return;
        if (s) {
          setSite(s);
          setSiteError(false);
        } else {
          setSite(null);
          setSiteError(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSite(null);
          setSiteError(true);
        }
      });
    workerService
      .getWorkerWeather(uuid)
      .then((groups) => {
        if (cancelled) return;
        setCurrentWeather(parseCurrentWeather(groups));
        setHourlyWeather(parseHourlyWeather(groups));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [uuid, validUuid]);

  if (!validUuid) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 p-6 text-center">
        <p className="text-sm text-neutral-500">
          현장 정보가 없습니다. 현장 입구의 QR 코드를 스캔해 주세요.
        </p>
      </main>
    );
  }

  if (siteError && !site) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 p-6 text-center">
        <p className="text-sm text-neutral-500">
          현장 정보를 불러올 수 없습니다.
          <br />
          현장 입구의 QR 코드를 다시 스캔하거나 잠시 후 다시 시도해 주세요.
        </p>
      </main>
    );
  }

  if (justInstalled) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-5 bg-slate-100 p-8 text-center">
        <img
          src={`${import.meta.env.BASE_URL}icons/icon-192.png`}
          alt="SAFERS"
          className="h-24 w-24 rounded-3xl shadow-md"
        />
        <div>
          <div className="text-xl font-bold text-neutral-800">설치 완료</div>
          <p className="mt-2 text-sm leading-relaxed text-neutral-500">
            이 브라우저 창을 닫고
            <br />홈 화면의 <b className="text-brand">SAFERS</b> 아이콘으로 열어 주세요.
            <br />
            위험 알림은 설치한 앱에서 받을 수 있습니다.
          </p>
        </div>
      </main>
    );
  }

  if (loading) return <WorkerSkeleton />;

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col gap-4 bg-slate-100 p-4">
      {showInstallModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex justify-center">
              <img
                src={`${import.meta.env.BASE_URL}icons/icon-192.png`}
                alt="SAFERS"
                className="h-16 w-16 rounded-2xl"
              />
            </div>
            <div className="mb-1 text-center text-lg font-bold text-neutral-800">앱 설치하기</div>
            <p className="mb-5 text-center text-sm leading-relaxed text-neutral-500">
              홈 화면에 앱을 설치한 뒤 이용해 주세요.
              <br />
              위험 알림은 <b>설치한 앱에서만</b> 받을 수 있습니다.
            </p>
            {platform === "installable" ? (
              <button
                type="button"
                onClick={promptInstall}
                className="mb-2 w-full rounded-xl bg-brand py-3 font-semibold text-white"
              >
                홈 화면에 추가
              </button>
            ) : platform === "ios" ? (
              <p className="mb-2 rounded-xl bg-slate-50 px-4 py-3 text-center text-sm leading-relaxed text-neutral-600">
                하단 <b>공유</b> → <b>홈 화면에 추가</b>를 누른 뒤<br />홈 아이콘으로 열어 주세요.
              </p>
            ) : (
              <p className="mb-2 rounded-xl bg-slate-50 px-4 py-3 text-center text-sm leading-relaxed text-neutral-600">
                브라우저 메뉴(⋮) → <b>홈 화면에 추가</b>로<br />
                앱을 설치해 주세요.
              </p>
            )}
            <button
              type="button"
              onClick={() => setInstallModalDismissed(true)}
              className="w-full py-2 text-sm text-neutral-400"
            >
              나중에
            </button>
          </div>
        </div>
      )}

      {/* 알림 받기 유도 — 딤 마스크 모달 (설치된 앱에서만) */}
      {showNotifyPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-1 text-center text-lg font-bold text-neutral-800">
              🔔 위험 알림 받기
            </div>
            <p className="mb-5 text-center text-sm leading-relaxed text-neutral-500">
              현장의 위험 경보·공지를 기기 알림으로 받으세요.
              <br />한 번만 설정하면 됩니다.
            </p>
            <button
              type="button"
              onClick={subscribe}
              disabled={busy}
              className="mb-2 w-full rounded-xl bg-brand py-3 font-semibold text-white disabled:opacity-50"
            >
              {busy ? "설정 중…" : "알림 받기"}
            </button>
            <button
              type="button"
              onClick={() => setPromptDismissed(true)}
              className="w-full py-2 text-sm text-neutral-400"
            >
              나중에
            </button>
          </div>
        </div>
      )}

      {/* 헤더 */}
      <header className="rounded-2xl bg-brand p-4 text-white">
        <div className="text-xs opacity-80">SAFERS 현장 안전정보</div>
        <div className="mt-1 text-lg font-bold">{site?.name ?? "현장"}</div>
      </header>

      {/* 방금 도착한 관리자 안내 배너 (#171) */}
      {banner && (
        <section className="rounded-2xl border-2 border-brand bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-brand/10 text-xs font-extrabold text-brand">
              !
            </span>
            <span className="text-xs font-bold text-brand">{banner.title}</span>
            <span className="ml-auto text-[0.6875rem] text-neutral-400">방금</span>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-neutral-800">
            {banner.message}
          </p>
          <button
            type="button"
            onClick={dismissBanner}
            className="mt-3 w-full rounded-xl bg-brand py-2.5 text-sm font-semibold text-white"
          >
            확인
          </button>
        </section>
      )}

      {/* 앱 설치 배너 (설치 완료 시 자동 숨김 · 눈에 띄는 브랜드 배너) */}
      {platform !== "installed" && !installDismissed && (
        <section className="rounded-2xl bg-brand p-4 text-white shadow-md">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white/20 text-xl">
              📲
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold">앱으로 설치하기</div>
              {platform === "installable" ? (
                <p className="mt-0.5 text-xs leading-relaxed text-white/85">
                  홈 화면에 추가하면 더 빠르게 열고 알림을 안정적으로 받아요.
                </p>
              ) : platform === "ios" ? (
                <p className="mt-0.5 text-xs leading-relaxed text-white/90">
                  하단 <b>공유</b> → <b>홈 화면에 추가</b> 후 홈 아이콘으로 열어주세요. 알림은 설치
                  후 받을 수 있어요 (iOS).
                </p>
              ) : (
                <p className="mt-0.5 text-xs leading-relaxed text-white/90">
                  브라우저 메뉴(⋮) → <b>홈 화면에 추가</b>로 설치할 수 있어요.
                </p>
              )}
              {platform === "installable" && (
                <button
                  type="button"
                  onClick={promptInstall}
                  className="mt-2.5 w-full rounded-xl bg-white py-2.5 text-sm font-bold text-brand"
                >
                  홈 화면에 추가
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setInstallDismissed(true)}
              aria-label="설치 안내 닫기"
              className="flex-none text-lg leading-none text-white/70"
            >
              ✕
            </button>
          </div>
        </section>
      )}

      {/* 최근 받은 안내 (#171) — 최신 1건 */}
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="mb-3 text-sm font-semibold text-neutral-500">최근 안내</div>
        {latestNotice ? (
          <>
            <p className="whitespace-pre-wrap break-words text-base leading-relaxed text-neutral-800">
              {latestNotice.message}
            </p>
            <div className="mt-2 text-xs text-neutral-400">
              {formatNoticeTime(latestNotice.receivedAt)}
            </div>
          </>
        ) : (
          <p className="py-2 text-center text-xs leading-relaxed text-neutral-400">
            아직 받은 안내가 없습니다.
            <br />
            현장 관리자가 보낸 안내가 여기에 표시됩니다.
          </p>
        )}
      </section>

      {/* 날씨 카드 */}
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="mb-3 text-sm font-semibold text-neutral-500">현재 날씨</div>
        <div className="flex items-center gap-4">
          <img src={weatherDesc.src} alt={weatherDesc.label} className="h-14 w-14 flex-none" />
          <div className="flex items-end gap-1">
            <span className="text-5xl font-bold text-neutral-800">
              {currentWeather?.temperature ?? "--"}
            </span>
            <span className="pb-2 text-xl text-neutral-400">°C</span>
          </div>
          {weatherDesc.label && (
            <span className="ml-auto rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-neutral-600">
              {weatherDesc.label}
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {currentWeather?.heatIndex && (
            <Metric label="체감" value={`${currentWeather.heatIndex}°`} />
          )}
          {currentWeather?.humidity && (
            <Metric label="습도" value={`${currentWeather.humidity}%`} />
          )}
          {currentWeather?.windSpeed && (
            <Metric
              label="바람"
              value={`${currentWeather.windDirection ?? ""} ${currentWeather.windSpeed}m/s`.trim()}
            />
          )}
          {currentWeather?.rainfall && currentWeather.rainfall !== "강수없음" && (
            <Metric label="강수" value={currentWeather.rainfall} />
          )}
        </div>

        {/* 시간별 */}
        <div className="mt-4 flex justify-between gap-1 overflow-x-auto">
          {hourlyWeather.map((h) => {
            const d = describeWeather(h.pty, h.sky, Number(h.hour));
            return (
              <div
                key={h.hour}
                className={`flex min-w-[2.75rem] flex-col items-center rounded-lg px-1 py-2 ${
                  h.isCurrent ? "bg-brand/10 font-semibold text-brand" : "text-neutral-500"
                }`}
              >
                <span className="text-xs">{h.isCurrent ? "지금" : `${h.hour}시`}</span>
                <img src={d.src} alt={d.label} className="mt-1 h-6 w-6" />
                <span className="mt-1 text-sm">{h.temp}°</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* 알림 받기 — 설치된 앱 · 권한 미설정 시에만 (구독/거부 시 숨김) */}
      {notifyEligible && (
        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="mb-1 text-sm font-semibold text-neutral-700">위험 알림 받기</div>
          <p className="mb-3 text-xs leading-relaxed text-neutral-500">
            현장의 위험 경보·공지를 휴대폰 알림으로 받습니다. 한 번만 설정하면 됩니다.
          </p>
          <button
            type="button"
            onClick={subscribe}
            disabled={busy}
            className="w-full rounded-xl bg-brand py-3 font-semibold text-white disabled:opacity-50"
          >
            {busy ? "설정 중…" : "알림 받기"}
          </button>
          {status && <div className="mt-2 break-all text-xs text-neutral-500">{status}</div>}
        </section>
      )}
    </main>
  );
}

function WorkerSkeleton() {
  return (
    <main className="mx-auto flex min-h-screen max-w-md animate-pulse flex-col gap-4 bg-slate-100 p-4">
      <div className="rounded-2xl bg-brand/60 p-4">
        <div className="h-3 w-24 rounded bg-white/40" />
        <div className="mt-2 h-5 w-40 rounded bg-white/50" />
      </div>
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="h-3 w-20 rounded bg-neutral-200" />
        <div className="mt-4 flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-neutral-200" />
          <div className="h-10 w-24 rounded bg-neutral-200" />
        </div>
        <div className="mt-4 flex gap-2">
          <div className="h-7 w-20 rounded-lg bg-neutral-100" />
          <div className="h-7 w-24 rounded-lg bg-neutral-100" />
        </div>
      </div>
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="h-3 w-16 rounded bg-neutral-200" />
        <div className="mt-3 h-4 w-full rounded bg-neutral-200" />
        <div className="mt-2 h-4 w-2/3 rounded bg-neutral-200" />
      </div>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded-lg bg-slate-50 px-3 py-1.5 text-sm text-neutral-700">
      <span className="text-neutral-400">{label}</span> {value}
    </span>
  );
}

/** 안내 수신 일시 표기 — 오늘이면 "HH:mm", 이전이면 "M/D HH:mm". */
function formatNoticeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay ? `${hh}:${mm}` : `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
}
